import {Component, OnDestroy, OnInit} from '@angular/core';
import {FamillesProduitService} from '../../core/services/entities/familles-produit.service';
import {FamilleProduitDTO} from '../../core/dtos/famille-produit-dto';
import {UtilsService} from '../../core/utils/utils.service';
import {HELP_FOLDERS, MSG_KEY, MSG_SEVERITY} from "../../core/constants";
import {Subscription} from "rxjs";
import {ResponseWrapper} from "../../core/suppliers/wrappers/response-wrapper";
import {confirm} from "devextreme/ui/dialog";
import {IsDeletableObject} from "../../core/models/is-deletable-object";
import {InternationalizationService} from "../../core/services/i8n/i8n.service";
import {ToastService} from "../../core/services/technique/toast.service";
import {ButtonThemeEnum} from '../../shared/ui/button/button-theme-enum';
import {escape as _escape} from 'lodash';

@Component({
    selector: 'yo-familles-produit',
    templateUrl: './familles-produit.component.html',
    styleUrls: ['./familles-produit.component.scss'],
    standalone: false
})

export class FamillesProduitComponent implements OnInit, OnDestroy {

  public readonly ButtonThemeEnum = ButtonThemeEnum;


  familleProduitList: FamilleProduitDTO[] = [];
  selectedFamilleProduit: FamilleProduitDTO;

  selectedRows: number[] = [];

  errors: any[] = [];
  displayErrors: boolean = false;

  private subQuery: Subscription;

  private subFamilleProduitSaved$: Subscription;

  pathFile: string = HELP_FOLDERS.REFERENTIEL + '/familles-produits/familles-produits';

  constructor(public familleProduitSvc: FamillesProduitService,
              private readonly i8nSvc: InternationalizationService,
              public utils: UtilsService,
              private readonly toastSvc: ToastService) {
    this.selectedFamilleProduit = new FamilleProduitDTO();
    this.selectedFamilleProduit.parent = new FamilleProduitDTO();
    this.familleProduitSvc.announceFamilleProduit(this.familleProduitList, this.selectedFamilleProduit);
  }

  ngOnInit() {
   this.initData();
   this.subscribeFamilleProduitSaved();
  }

  initData(){
    this.subQuery = this.familleProduitSvc.getAllFamilleProduits().subscribe((response: ResponseWrapper<FamilleProduitDTO>) => {
      this.familleProduitList = response.resultList;
    });
  }

  openDialogEdition = (familleProduit?: FamilleProduitDTO) => {
    this.familleProduitSvc.announceFamilleProduit(this.familleProduitList, familleProduit);
  };

  subscribeFamilleProduitSaved = (): void => {
    this.subFamilleProduitSaved$ = this.familleProduitSvc.familleProduitSaved$.subscribe(() => this.initData());
  }

  onSelectionChanged = (event : any) => {
    this.selectedFamilleProduit = event.selectedRowsData[0];
  };

  ngOnDestroy(): void {
    this.utils.unsubscribe(this.subQuery);
    this.utils.unsubscribe(this.subFamilleProduitSaved$);
  }

  canDelete = (): boolean => this.selectedRows?.length > 0;

  deleteValues = async (): Promise<void> => {
    this.errors = [];
    let idsToDeleteInTable = Object.assign([], this.selectedRows);

    let isDeletionValidated: boolean = await confirm(this.getDeletionQuestion(idsToDeleteInTable), this.i8nSvc.getLabelFromCode("Suppression", null));

    if (!isDeletionValidated)
      return;

    this.familleProduitSvc.delete(this.selectedRows).subscribe(response => {
      const res: any = (response as ResponseWrapper<IsDeletableObject>).one;

      if (res) {
        if (res?.messagesErrorList?.length) {
          for (const error of res.messagesErrorList) {
            // Une erreur sans argument (ex. FAMILLE_PRODUIT_DOES_NOT_EXIST) ne désigne aucune famille à garder dans la liste
            const infosLine: string = this.i8nSvc.getLabelFromCode(error.code, error.args ? [error.args] : null);
            this.errors.push({infosLine});
            const elementToKeep = error.args ? this.familleProduitList.find(u => u.code === error.args) : undefined;
            if (elementToKeep)
              idsToDeleteInTable = idsToDeleteInTable.filter(id => elementToKeep.id !== id);
          }
          this.displayDeleteErrors();
        } else {
          this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.SUCCESS, idsToDeleteInTable.length > 1 ? `Les familles ont bien été supprimées` : `La famille a bien été supprimée`);
        }
        // Les sous-familles sont supprimées en cascade avec leur famille
        const deletedIdList: number[] = [...idsToDeleteInTable, ...this.findSousFamilleList(idsToDeleteInTable).map(f => f.id)];
        this.familleProduitList = this.familleProduitList.filter(famille => !deletedIdList.includes(famille.id));
      }
    });
  }

  private getDeletionQuestion = (familleProduitIdList: number[]): string => {
    const sousFamilleList: FamilleProduitDTO[] = this.findSousFamilleList(familleProduitIdList);
    // confirm() affiche du HTML : les libellés saisis par les utilisateurs sont échappés
    if (sousFamilleList.length)
      return this.i8nSvc.getLabelFromCode("CONFIRM_DELETION_FAMILLE_PRODUIT_WITH_SOUS_FAMILLE", [_escape(sousFamilleList.map(f => f.libelle).join(', '))]);
    return this.i8nSvc.getLabelFromCode(familleProduitIdList.length > 1 ? "CONFIRM_DELETION_PLURAL" : "CONFIRM_DELETION_SINGULAR", null);
  }

  /** Sous-familles des familles sélectionnées, sans celles qui sont elles-mêmes sélectionnées. */
  private findSousFamilleList = (familleProduitIdList: number[]): FamilleProduitDTO[] =>
    familleProduitIdList
      .reduce((list: FamilleProduitDTO[], id) => list.concat(this.familleProduitSvc.findDescendantList(id, this.familleProduitList)), [])
      .filter((f, index, list) => !familleProduitIdList.includes(f.id) && list.findIndex(other => other.id === f.id) === index);

  displayDeleteErrors = (): void => {
    this.displayErrors = true;
  }

  closeErrors = (): void => {
    this.displayErrors = false;
  }

}
